import Vue from 'vue'
import Vuex from 'vuex'
import vuetify from '@/plugins/vuetify'
import { getInstance } from '@/auth/auth0-plugin'

// API
import kvApi from '@/axios/key-values-endpoint'
import PromoCategory from '@/axios/promo-category-endpoint'
import PartyCategoryRelationship from '@/axios/party-category-relationship-endpoints'
import Party from '@/axios/party-endpoint'
import Items from '@/axios/items-endpoint'
import PartyRelationship from '@/axios/party-relationship-endpoint'

Vue.use(Vuex)

const state = {
  InternalBillingParties: [],
  BillingModes: [],
  BillingSplitTypes: [],
  PromotionTypes: [],
  ResponsibleParties: [],
  PromoClassifications: [],
  PromoCategories: [],
  userRelatedParties: [],
  categoryManagers: [],
  adGroups: [],
  regionGroups: [],
  activeRegionGroups: [],
  distributionCenters: [],
  activeDistributionCenters: [],
  promoStatuses: [],
  userPromoCategoryIds: [],
  readOnlyCategories: ['AMAP', 'Early Buy Allowance'],
  adPromoOnlyExcludedGroupCodes: ['BPC', 'QPC',  'HHS'],
  totalPromoExcludedGroupCodes: ['CPC', 'CMA', 'CPG', 'BCW'],
  vendorHiddenCategories: {
    awg: [
      'AMAP',
      'Scan Fee',
      'Accrual Ad Billback',
      'TPR Non Allowance',
      'TPR Scan',
      'When You Buy',
      'Ad Scan Required Retail',
      'Hot Buy Scan',
      'Hot Buy Billback',
      'VMC Scan',
      'VMC Billback'
    ],
    'alliance-retail-group': [
      'Ad Billback - PCE Meat',
      'Ad Billback - Non Standard',
      'Ad Scan - ARG Spendback',
      'PCAF - Non Standard',
      'TPR Non Allowance',
      'Vendor Accrual Flat Rate',
      'Vendor Accrual Scan Based',
      'Vendor Accrual Shipment Based'
    ]
  },
  pceHiddenCategories: [
    'Limited Billback'
  ]
}

const mutations = {
  commitInternalBillingParties (state, payload) {
    state.InternalBillingParties = payload
  },
  commitBillingModes (state, payload) {
    state.BillingModes = payload
  },
  commitBillingSplitTypes (state, payload) {
    state.BillingSplitTypes = payload
  },
  commitPromoTypes (state, payload) {
    state.PromotionTypes = payload
  },
  commitResponsibleParties (state, payload) {
    state.ResponsibleParties = payload
  },
  commitPromoClassifications (state, payload) {
    state.PromoClassifications = payload
  },
  commitPromoCategories (state, payload) {
    state.PromoCategories = payload
  },
  commitUserRelatedParties (state, payload) {
    state.userRelatedParties = payload
  },
  commitCategoryManagers (state, payload) {
    state.categoryManagers = payload
  },
  commitPromoStatusValues (state, payload) {
    state.promoStatuses = payload
  },
  commitUserPromoCategoryIds (state, payload) {
    state.userPromoCategoryIds = payload
  },
  commitAdGroups (state, payload) {
    state.adGroups = payload
  },
  commitRegionGroups (state, payload) {
    state.regionGroups = payload
  },
  commitActiveRegionGroups (state, payload) {
    state.activeRegionGroups = payload
  },
  commitDistributionCenters (state, payload) {
    state.distributionCenters = payload
  },
  commitActiveDistributionCenters (state, payload) {
    state.activeDistributionCenters = payload
  },
  setTheme(state, theme) {
    vuetify.framework.theme.themes.light = vuetify.userPreset.theme.themes[theme]
  }
}

const actions = {
  async initParties (context) {
    const authService = getInstance()
    const actions = [
      'getAdGroups'
    ]
    if (['pricechopper', 'alliance-retail-group'].includes(authService.tenant)) {
      actions.push('getCategoryManagers')
    }
    if (authService.tenant === 'jbg' || authService.tenant === 'alliance-retail-group') {
      actions.push('getDistributionCenters')
    }
    if (authService.tenant === 'alliance-retail-group') {
      actions.push('getRegionGroups')
      actions.push('getInternalBillingParties')
    }
    const promises = actions.map(action => {
      return context.dispatch(action)
    })
    try {
      const results = await Promise.allSettled(promises)
      const rejected = results.filter(r => r.status === 'rejected')
      if (rejected.length > 0) throw rejected
    } catch (err) {
      console.error(err)
    }
  },
  async getPromoKeyValues (context) {
    const kvActions = [
      'getBillingModes',
      'getBillingSplitTypes',
      'getPromotionTypes',
      'getPromotionClassifications',
      'getPromoStatusValues'
    ]
    try {
      const promises = kvActions.map(action => {
        return context.dispatch(action)
      })
      const results = await Promise.allSettled(promises)
      const rejected = results.filter(r => r.status === 'rejected')
      if (rejected.length > 0) throw rejected
    } catch (err) {
      console.error(err)
    }
  },
  async getBillingModes (context) {
    const billing_modes = context.getters.billing_modes
    if (billing_modes.length === 0) {
      return kvApi.keyValues.getList('BILLING_MODE', 50, 0)
				.then((response) => {
          context.commit('commitBillingModes', response.data)
				}).catch(err => {
          throw err
        })
    }
  },
  async getInternalBillingParties (context) {
    const internal_billing_parties = context.getters.internal_billing_parties
    if (internal_billing_parties.length === 0) {
      return kvApi.keyValues.getList('INTERNAL_BILLING_PARTY', 50, 0)
				.then((response) => {
          context.commit('commitInternalBillingParties', response.data)
				}).catch(err => {
          throw err
        })
    }
  },
  async getBillingSplitTypes (context) {
    const billing_split = context.getters.billing_split
    if (billing_split.length === 0) {
      return kvApi.keyValues.getList('BILLING_SPLIT_TYPE', 50, 0)
        .then((response) => {
          context.commit('commitBillingSplitTypes', response.data)
        }).catch(err => {
          throw err
        })
    }
  },
  async getPromotionTypes (context) {
    const promo_types = context.getters.promo_types
    if (promo_types.length === 0) {
      return kvApi.keyValues.getList('PROMO_TYPE', 50, 0)
				.then((response) => {
          if (response.data.length > 0) {
            response.data.sort((a, b) => (a.display_order - b.display_order))
          }
          context.commit('commitPromoTypes', response.data)
				}).catch(err => {
          throw err
        })
    }
  },
  async getPromotionClassifications (context) {
    const promo_classes = context.getters.promo_classes
    if (promo_classes.length === 0) {
      return kvApi.keyValues.getList('CLASSIFICATION', 50, 0)
				.then((response) => {
          context.commit('commitPromoClassifications', response.data)
				}).catch(err => {
          throw err
        })
    }
  },
  async getPromoStatusValues (context) {
    const promo_statuses = context.getters.promo_statuses
    if (promo_statuses.length === 0) {
      return kvApi.keyValues.getList('STATUS', 50, 0).then(response => {
        if (response.data.length > 0) {
          response.data.sort((a, b) => (a.display_order - b.display_order))
        }
        context.commit('commitPromoStatusValues', response.data)
      }).catch(err => {
        throw err
      })
    }
  },
  async getPromoCategories (context) {
    const promo_categories = context.getters.promo_categories
    const argHiddenCategories = context.getters.argHiddenCategories
    const authService = getInstance()
    if (promo_categories.length === 0) {
      return PromoCategory.getList()
				.then((response) => {
          if (response.data.length > 0) {
            response.data = response.data.filter(c => {
              return c.name.toLowerCase().includes('lump sum') === false
            }).sort((a, b) => (a.display_order - b.display_order))
          }
          if (authService.tenant === 'alliance-retail-group') {
            response.data = response.data.filter((c) => {
              return !argHiddenCategories.includes(c.name)
            })
          }
          context.commit('commitPromoCategories', response.data)
        }).catch(err => {
          throw err
        })
    }
  },
  async getResponsibleParties(context) {
    let parties = []
    if (context.getters.responsible_parties?.length > 0) return
    try {
      const res = await Party.getWholesalers()
      parties = res?.data || []
      return parties.length > 0 ? context.dispatch('getSourceRels', parties) : []
    } catch (err) {
      console.error(err)
    }
  },
  async getSourceRels (context, parties) {
    if (parties.length === 1) {
      const party = parties[0]
      const res = await Items.getSourceRelsByPartyId(party.id)
      party.sources = res?.data || []
      return context.commit('commitResponsibleParties', parties)
    }
    const res = await Promise.allSettled(parties.map(party => {
      return Items.getSourceRelsByPartyId(party.id)
    }))
    const mapped = parties.map((party, i) => {
      const result = res[i]
      if (result?.status === 'rejected') {
        throw result.reason
      } else {
        party.sources = result?.value?.data || []
      }
      return party
    })
    return context.commit('commitResponsibleParties', mapped)
  },
  async getUserRelatedParties (context) {
    const userParties = context.getters.userRelatedParties
    if (userParties.length > 0) return

    const authService = getInstance()
    const { tenant, permissions, user: { email } } = authService
    const limit_subvendors = (tenant === 'alliance-retail-group') && permissions?.includes('promo:related_subvendors')

    let relatedParties = []
    try {
      const res = await Party.getUserProfile({ email, limit_subvendors })
      if (res?.data?.length > 0) {
        relatedParties = res.data
      }
    } catch (err) {
      console.error(err)
    }
    return context.commit('commitUserRelatedParties', relatedParties)
  },
  async getCategoryManagers (context) {
    let categoryManagers = []
    try {
      const res = await Party.getCategoryManagers()
      if (res?.data?.length > 0) {
        categoryManagers = res.data.sort((a, b) => a.name.localeCompare(b.name))
      }
    } catch (err) {
      console.error(err)
    }
    return context.commit('commitCategoryManagers', categoryManagers)
  },
  async getUserCategories (context) {
    const relatedParties = context.getters.userRelatedParties
    if (relatedParties.length > 0) {
      const vendorPartyIds = relatedParties.flatMap(party => {
        return (party?.party_type_constant === 'VENDOR') ? party.id : []
      })
      const promises = vendorPartyIds.map(partyId => {
        return PartyCategoryRelationship.getRelationshipsByParty(partyId)
      })
      try {
        const results = await Promise.allSettled(promises)
        const rejected = results.filter(r => r.status === 'rejected')
        if (rejected.length > 0) throw rejected

        const categoryIds = results.flatMap(r => {
          if (r.status === 'fulfilled' && r.value?.data?.length > 0) {
            return r.value.data.map(rel => rel.promo_category_id)
          }
          return []
        })
        context.commit('commitUserPromoCategoryIds', categoryIds)
      } catch (err) {
        console.error(err)
      }
    }
  },
  async getAdGroups (context) {
    if (context.getters.adGroups.length > 0) return
    let adGroups = []
    try {
      const res = await Party.getAdGroups()
      adGroups = res?.data || []
      context.commit('commitAdGroups', adGroups)
    } catch (err) {
      console.error(err)
    }
  },
  async getRegionGroups (context) {
    const authService = getInstance()
    if (context.getters.regionGroups.length > 0) return
    let regionGroups = []
    try {
      const res = await Party.getRegionGroups()
      regionGroups = res?.data || []
      context.commit('commitRegionGroups', regionGroups)
      if (authService.tenant === 'alliance-retail-group') {
        context.dispatch('getActiveRegionGroups')
      }
    } catch (err) {
      console.error(err)
    }
  },
  async getActiveRegionGroups (context) {
    if (context.getters.activeRegionGroups?.length > 0) return
    let regionGroups = context.getters.regionGroups
    const authService = getInstance()
    try {
      if (regionGroups?.length > 0) {
        let regionGroupPromises = await Promise.allSettled(regionGroups.flatMap(async rg => {
          const memberPartyIds = []
          const generator = PartyRelationship.genPartyRelationships(rg.id, 0, 200, true)
          for await (const results of generator) {
            if (results?.length > 0) {
              const ids = results.flatMap(rel => {
                if (rel['to_party']?.party_type?.constant === 'AD_GROUP') {
                  return rel['to_party'].id
                }
                return []
              })
              memberPartyIds.push(...ids)
            }
          }
          const activeAdGroups = context.getters.adGroups.filter((g) => g?.attributes?.AD_GROUP_IS_ACTIVE === 'True')
          rg.member_ids = memberPartyIds.filter(id => activeAdGroups.some(active_ag => id === active_ag.id))
          if (authService.tenant === 'alliance-retail-group') {
            const promoExcludedGroups = context.getters.totalPromoExcludedAdGroups
            rg.member_ids = [...rg.member_ids].filter(id => !promoExcludedGroups.some(ag => ag.id === id))
          }
          rg.party_id = rg.id
          return rg
        }))
        regionGroups = regionGroupPromises.flatMap(result => {
          if (result.status !== 'rejected') return result.value
        })
      }
      const activeRegionGroups = regionGroups.filter(rg => rg.member_ids.length > 0)
      return context.commit('commitActiveRegionGroups', activeRegionGroups)
    } catch (err) {
      console.error(err)
    }
  },
  async getDistributionCenters (context) {
    if (context.getters.distributionCenters.length > 0) return
    const authService = getInstance()
    let distributionCenters = []
    try {
      const res = await Party.getDistributionCenters()
      if (res?.data?.length > 0) {
        distributionCenters = res.data.sort((a, b) => a.name.localeCompare(b.name))
      }
      context.commit('commitDistributionCenters', distributionCenters)
      if (authService.tenant === 'alliance-retail-group') {
        context.dispatch('getActiveDistributionCenters')
      }
    } catch (err) {
      console.error(err)
    }
  },
  async getActiveDistributionCenters (context) {
    if (context.getters.activeDistributionCenters?.length > 0) return
    let distributionCenters = context.getters.distributionCenters
    const authService = getInstance()
    try {
      if (distributionCenters?.length > 0) {
        let distributionCenterPromises = await Promise.allSettled(distributionCenters.flatMap(async dc => {
          const memberPartyIds = []
          const res = await PartyRelationship.downstreamRelationshipSearch({ excluded_downstream_types: ['STORE'], to_party_ids: [dc.id] })
          if (res?.data?.length > 0) {
            const ids = res.data.flatMap(rel => {
              if (rel['from_rel_type'] === 'AD_GROUP_HAS_PARENT') {
                return rel.from_party_id
              }
              return []
            })
            memberPartyIds.push(...ids)
          }
          const activeAdGroups = context.getters.adGroups.filter((g) => g?.attributes?.AD_GROUP_IS_ACTIVE === 'True')
          dc.member_ids = memberPartyIds.filter(id => activeAdGroups.some(active_ag => id === active_ag.id))
          if (authService.tenant === 'alliance-retail-group') {
            const promoExcludedGroups = context.getters.totalPromoExcludedAdGroups
            dc.member_ids = [...dc.member_ids].filter(id => !promoExcludedGroups.some(ag => ag.id === id))
          }
          dc.party_id = dc.id
          return dc
        }))
        distributionCenters = distributionCenterPromises.flatMap(result => {
          if (result.status !== 'rejected') return result.value
        })
      }
      const activeDCs = distributionCenters.filter(dc => dc.member_ids.length > 0 && dc.attributes.DC_IS_ACTIVE.toLowerCase() === 'true')
      return context.commit('commitActiveDistributionCenters', activeDCs)
    } catch (err) {
      console.error(err)
    }
  }
}

const getters = {
  email () {
    const authService = getInstance()
    if (authService.isAuthenticated) {
      return authService.user.email
    }
    return ''
  },
  internal_billing_parties (state) {
    return state.InternalBillingParties
  },
  billing_modes (state) {
    return state.BillingModes
  },
  billing_split (state) {
    return state.BillingSplitTypes
  },
  promo_types (state) {
    return state.PromotionTypes
  },
  promo_classes (state) {
    return state.PromoClassifications
  },
  promo_categories (state) {
    return state.PromoCategories
  },
  promo_statuses (state) {
    return state.promoStatuses
  },
  responsible_parties (state) {
    return state.ResponsibleParties
  },
  sources (state) {
    return state.ResponsibleParties.flatMap(party => party?.sources || [])
  },
  getSourceName: (state, getters) => source_id => {
    const match = getters.sources.find(s => s?.source?.source_id === source_id)
    return match?.source?.source_name || source_id
  },
  userRelatedParties (state) {
    return state.userRelatedParties
  },
  userRelatedPartyIds (state) {
    if (state.userRelatedParties.length > 0) {
      return state.userRelatedParties.map(party => party.id)
    }
    return []
  },
  userPromoCategoryIds (state) {
    return state.userPromoCategoryIds
  },
  vendorAccrualCategories (state) {
    return state.PromoCategories.filter(c => c?.name?.toLowerCase()?.includes('vendor accrual'))
  },
  argHiddenCategories () {
    // TO DO: remove when ready for prod
    return (process.env.NODE_ENV === 'production')
      ? ['Vendor Accrual Flat Rate', 'Vendor Accrual Scan Based', 'Vendor Accrual Shipment Based']
      : []
  },
  adGroups (state) {
    return state.adGroups
  },
  activeAdGroups (state) {
    return state.adGroups.filter((g) => g?.attributes?.AD_GROUP_IS_ACTIVE === 'True')
  },
  totalPromoExcludedAdGroups (state) {
    return state.adGroups.filter(ag => state.totalPromoExcludedGroupCodes.includes(ag?.attributes?.AD_GROUP_CODE))
  },
  adPromoOnlyExcludedAdGroups (state) {
    return state.adGroups.filter(ag => state.adPromoOnlyExcludedGroupCodes.includes(ag?.attributes?.AD_GROUP_CODE))
  },
  regionGroups (state) {
    return state.regionGroups
  },
  activeRegionGroups (state) {
    return state.activeRegionGroups
  },
  categoryManagers () {
    return state.categoryManagers
  },
  restrictBallsQueensSelection () {
    const authService = getInstance()
    const adminRoles = ['promo.admin', 'client-admin', 'pce-admin', 'jbg-admin', 'ipro-admin']
    const isPrivileged = adminRoles.some(role => authService.userRoles?.includes(role))
    return authService.tenant === 'alliance-retail-group'
      && !isPrivileged
      && authService.permissions?.includes('promo:related_vendors')
  },
  distributionCenters (state) {
    return state.distributionCenters
  },
  activeDistributionCenters (state) {
    return state.activeDistributionCenters
  }
}

export const store = new Vuex.Store({
  state,
  actions,
  getters,
  mutations
})
